import { LocalStorageVariables } from "../entities/type";
import { isLoggingOut } from "./sessionTeardown";

/**
 * Single owner of `redirect_url` — "where do we send the user once they log in".
 *
 * It used to be written from two places with contradictory rules: the 401 path
 * in AppApiManager kept the FIRST value ("with several tabs 401-ing at once, the
 * first one wins"), while PrivateRoute overwrote unconditionally. The guard
 * never actually held: a 401 redirects to /login, that render goes through
 * PrivateRoute, and PrivateRoute clobbered the value the guard had just
 * protected. Both now go through `rememberRedirectUrl`.
 *
 * The policy is LAST WRITE WINS. First-wins reads as the safer choice but is
 * worse in the common case: the key survives an abandoned login (bounced to
 * /auth, tab closed without signing in), so a later deep link to a different
 * page would be discarded in favour of the stale one and drop the user
 * somewhere they never asked for. The most recent navigation is the best
 * evidence of intent, and the key is cleared on successful login (SignInForm)
 * and on logout (AuthProvider), so it does not accumulate.
 */

// Paths that already are (or resolve to) the sign-in screen. Never worth saving
// as a post-login destination, and never worth bouncing to /login from.
export const AUTH_PATHS = ["/", "/auth", "/login", "/reset-password"];

export const isAuthPath = (pathname: string): boolean =>
  AUTH_PATHS.includes(pathname);

/**
 * True when a redirect destination must not be recorded: either the session is
 * being torn down (the user just logged out — saving the page they are leaving
 * makes the next login do a full reload instead of a clean redirect), or we are
 * already sitting on the sign-in screen, which is not a destination.
 */
export const shouldSkipRedirectUrl = (): boolean =>
  isLoggingOut() || isAuthPath(window.location.pathname);

/**
 * Record where to return after login. Defaults to the current location, which
 * is what every caller wants except PrivateRoute, which knows the route it is
 * refusing before the browser has moved there.
 */
export const rememberRedirectUrl = (path?: string): void => {
  if (shouldSkipRedirectUrl()) {
    return;
  }

  localStorage.setItem(
    LocalStorageVariables.REDIRECT_URL,
    path ?? `${window.location.pathname}${window.location.search}`
  );
};
